import { FormLayoutElement } from "@/config/forms/general";
import {
  Artwork,
  ArtworkInExhibition,
  Exhibition,
  ItinerantExhibitionParentType,
} from "@/lib/types";
import ArtworkInExhibitionDrawer from "./ArtworkInExhibitionDrawer";
import { itinerantStopFromExhibition } from "./ItinerantExhibitionPanel";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string;
  parentType: ItinerantExhibitionParentType;
  queryId: string;
  type: string;
  fields?: FormLayoutElement[];
  exhibition: Exhibition;
  artwork?: Artwork;
  existingLink?: ArtworkInExhibition | null;
  allLinks?: ArtworkInExhibition[];
  complexRelationKey?: string;
  showCatalogNumber?: boolean;
  showExemplaireExpose?: boolean;
  forceClassic?: boolean;
};

function isItinerantFlag(
  value: boolean | string | number | undefined | null,
): boolean {
  return (
    value === true || value === "true" || value === "t" || value === 1
  );
}

/**
 * Always opens the classic create/edit link drawer; when the exhibition is
 * itinerant, injects itinerantConfig so the drawer widens with a left panel.
 */
export function ExhibitionLinkDrawerSwitch({
  open,
  onOpenChange,
  parentId,
  parentType,
  queryId,
  type,
  fields,
  exhibition,
  artwork,
  existingLink,
  allLinks,
  complexRelationKey = "artworkInExhibitions",
  showCatalogNumber = true,
  showExemplaireExpose = false,
  forceClassic,
}: Props) {
  const groupId =
    existingLink?.itinerantGroupId ||
    allLinks?.find((l) => l.id === existingLink?.id)?.itinerantGroupId ||
    allLinks?.find((l) => l.exhibition?.id === exhibition.id)?.itinerantGroupId ||
    null;
  const isItinerant =
    !forceClassic &&
    (Boolean(groupId) ||
      isItinerantFlag(exhibition.expositionsDescriptionExpositionItinerante));

  let itinerantConfig: ItinerantDrawerConfig | undefined;
  if (isItinerant) {
    const initialStops =
      groupId && allLinks
        ? allLinks
            .filter((l) => l.itinerantGroupId === groupId)
            .sort((a, b) => (a.itinerantOrder ?? 0) - (b.itinerantOrder ?? 0))
            .map((l) =>
              itinerantStopFromExhibition(l.exhibition, l.id, {
                catalogNumber:
                  l.oeuvresDansExpositionsDescriptionNumeroCatalogue ?? "",
                note: l.itinerantNote ?? "",
              }),
            )
        : undefined;

    itinerantConfig = {
      parentType,
      parentId,
      groupId,
      seedExhibition: exhibition,
      initialStops,
      complexRelationKey,
      showCatalogNumber,
      showExemplaireExpose,
    };
  }

  return (
    <ArtworkInExhibitionDrawer
      fields={fields}
      queryId={queryId}
      type={type}
      open={open}
      onOpenChange={onOpenChange}
      itinerantConfig={itinerantConfig}
      data={
        existingLink ?? {
          id: "",
          artwork: artwork!,
          exhibition,
          oeuvresDansExpositionsDescriptionNumeroCatalogue: "",
          oeuvresDansExpositionsNotes: "",
        }
      }
    />
  );
}
